import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { IBE_SIDEBAR_APPOINTMENT_READ_PATTERN, ibeSidebarAppointmentHistoryRead, ibeSidebarAppointmentRead, ibeSidebarAppointmentSmsRead } from 'actions/appointment/beSidebarAppointment.action';
import { LT_PUSHER } from 'bl/loggers.bl';
import HiddenDiv from 'components/core/HiddenDiv.component';
import H from 'constants/httpMethod.const';
import { useStaleAppointmentIds } from 'providers/Pusher.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import logger from 'utils/logger.util';
import isNotFullArray from 'utils/predicates/array/isNotFullArray.util';

const L = logger(['CacheBusterWatcher', LT_PUSHER]); // eslint-disable-line no-unused-vars

const USE_TEST_STATIC_ENDPOINT_CONFIGURATOR = false;

const USE_NUCLEAR_OPTION = false;
const USE_INVALIDATE = false;
const USE_REFETCH = true;

const MISSING_PATH = '?';
const AT_READ_AP = ibeSidebarAppointmentRead.AT.name;
const AT_READ_HIST = ibeSidebarAppointmentHistoryRead.AT.name;
const AT_READ_SMS = ibeSidebarAppointmentSmsRead.AT.name;

if (USE_TEST_STATIC_ENDPOINT_CONFIGURATOR) {
  L.debug(
    'ibeSidebarAppointmentRead:',
    ibeSidebarAppointmentRead(ibeSidebarAppointmentRead.AT.name, H.get, IBE_SIDEBAR_APPOINTMENT_READ_PATTERN, {
      params: { salonId: 1234, appointmentId: 5678 },
    }),
  );
}

const bust = (options) => {
  const FTAG = 'bust()';

  let stage = 0;
  try {
    const { staleAppointmentIds, salonId, qc } = options ?? {};

    if (isNotFullArray(staleAppointmentIds)) {
      stage = 1;
      L.debug(FTAG, 'Nothing to update, ids:', staleAppointmentIds);
      return;
    }

    stage = 2;
    const ids = JSON.stringify(staleAppointmentIds);
    const before = qc
      .getQueryCache()
      .getAll()
      .map((c) => c.queryKey).length;

    stage = 3;
    L.debug(FTAG, 'updating ids:', ids, '... Cache size before:', before);

    qc.removeQueries({ queryKey: [AT_READ_AP, void 1] });
    qc.removeQueries({ queryKey: [AT_READ_AP, null] });
    qc.removeQueries({ queryKey: [AT_READ_AP, salonId, H.get, MISSING_PATH] });

    stage = 4;
    const refetchKeys = staleAppointmentIds.flatMap((appointmentId) => [
      [AT_READ_AP, salonId, H.get, `salon/:salonId/fetch/appointment/${appointmentId}/`],
      [AT_READ_HIST, salonId, H.get, `v2/salon/:salonId/appointment/log/${appointmentId}/`],
      [AT_READ_SMS, salonId, H.get, `salon/v2/:salonId/sms/appointment/${appointmentId}/`],
    ]);

    const keys = JSON.stringify(refetchKeys);
    L.debug(FTAG, 'updating keys:', keys, { i: USE_INVALIDATE, r: USE_REFETCH, n: USE_NUCLEAR_OPTION }, '...');

    stage = 5;
    if (USE_NUCLEAR_OPTION) {
      L.debug(FTAG, 'Using nuclear option: qc.getQueryCache().clear()...');
      qc.getQueryCache().clear();
    }

    stage = 6;
    if (USE_INVALIDATE) {
      for (const queryKey of refetchKeys) {
        L.unignore(queryKey, 'invalidate:', qc.invalidateQueries({ queryKey, refetchType: 'all', type: 'all' }));
      }
    }

    stage = 7;
    if (USE_REFETCH) {
      for (const queryKey of refetchKeys) {
        L.unignore(queryKey, 'refetch:', qc.refetchQueries({ queryKey, type: 'all' }));
      }
    }

    stage = 8;
    const after = qc
      .getQueryCache()
      .getAll()
      .map((c) => c.queryKey).length;

    stage = 9;
    L.debug(FTAG, 'Done updating ids:', ids, '! Cache size after:', after);
  } catch (e) {
    L.warn(FTAG, e);
  } finally {
    L.debug(FTAG, 'Completed stage:', stage);
  }
};

const CacheBusterWatcher = () => {
  const salonId = useCurrentSalonId();
  const qc = useQueryClient();
  const staleAppointmentIds = useStaleAppointmentIds();

  useEffect(() => void bust({ staleAppointmentIds, qc, salonId }), [staleAppointmentIds, qc, salonId]);

  return <HiddenDiv tag="CacheBusterWatcher--hidden" />;
};

export default CacheBusterWatcher;
